/* eslint-disable sonarjs/cognitive-complexity */
import { Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useNotifications } from '@mantine/notifications';
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';

import { ToastContext } from './context';
import ToastManager from './manager';
import Toast from './toast';
import { TOAST_TYPE, type ToastProps } from './types';

export default function ToastProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const isMobile = useMediaQuery(`(max-width: 767px)`);
  const notificationRef = useRef<HTMLDivElement | null>(null);

  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const instance = ToastManager.getInstance();
  const notificationsStore = useNotifications();

  const getNotificationHeight = useCallback(() => {
    if (notificationsStore.notifications?.length && notificationRef.current) {
      return notificationRef.current.getBoundingClientRect().height;
    }
    return 0;
  }, [notificationsStore.notifications]);

  useEffect(() => {
    return instance.subscribe(setToasts);
  }, [instance]);

  const addToast = useCallback(
    (toast: ToastProps) => instance.addToast(toast),
    [instance]
  );

  const removeToast = useCallback(
    (id: string) => instance.removeToast(id),
    [instance]
  );

  const showSuccess = useCallback(
    (toast: ToastProps) => addToast({ ...toast, type: TOAST_TYPE.SUCCESS }),
    [addToast]
  );

  const showError = useCallback(
    (toast: ToastProps) => addToast({ ...toast, type: TOAST_TYPE.ERROR }),
    [addToast]
  );

  const showWarning = useCallback(
    (toast: ToastProps) => addToast({ ...toast, type: TOAST_TYPE.WARNING }),
    [addToast]
  );

  const value = useMemo(
    () => ({
      toasts,
      addToast,
      removeToast,
      showSuccess,
      showError,
      showWarning,
    }),
    [addToast, removeToast, showError, showSuccess, showWarning, toasts]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts && toasts.length > 0 ? (
        <Stack
          ref={notificationRef}
          className="w-600 fixed left-1/2 z-50 flex items-center space-x-4 divide-x rounded-md"
          style={{
            transform: 'translateX(-50%)',
            top: isMobile
              ? `${getNotificationHeight() > 0 ? getNotificationHeight() + 70 : 50}px`
              : 'auto',
            bottom: !isMobile
              ? `${getNotificationHeight() > 0 ? getNotificationHeight() + 60 : 20}px`
              : 'auto',
          }}
        >
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              {...toast}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </Stack>
      ) : null}
    </ToastContext.Provider>
  );
}
